import { useEffect, useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api, apiError } from "@/lib/api";
import { toast } from "sonner";
import { ArrowLeft, Check, X, Clock, Phone, Mail, Users, Calendar, MessageCircle } from "lucide-react";

const STATUS_META = {
  present: { cls: "bg-brand-blue/20 text-brand-blue border-brand-blue/40", label: "Present" },
  late:    { cls: "bg-yellow-500/20 text-yellow-400 border-yellow-400/40", label: "Late" },
  absent:  { cls: "bg-red-500/20 text-red-400 border-red-400/40",         label: "Absent" },
};

export default function CoachBatchDetail() {
  const { batchId } = useParams();
  const [data, setData] = useState(null);
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [attendance, setAttendance] = useState({}); // ward_id -> status
  const [loading, setLoading] = useState(true);
  const [pinging, setPinging] = useState(false);

  const loadRoster = () => {
    setLoading(true);
    api.get(`/coach/batches/${batchId}/roster`)
      .then((r) => setData(r.data))
      .catch(() => toast.error("Cannot load this batch — you may not be assigned to it."))
      .finally(() => setLoading(false));
  };

  const loadAttendance = () => {
    api.get("/coach/attendance", { params: { batch_id: batchId, date } })
      .then((r) => {
        const m = {};
        r.data.forEach((a) => { m[a.ward_id] = a.status; });
        setAttendance(m);
      });
  };

  useEffect(() => { loadRoster(); }, [batchId]);
  useEffect(() => { if (data) loadAttendance(); }, [batchId, date, data]);

  const mark = async (wardId, status) => {
    try {
      await api.post("/coach/attendance", { ward_id: wardId, batch_id: batchId, date, status });
      setAttendance((m) => ({ ...m, [wardId]: status }));
      toast.success(`${STATUS_META[status].label}`);
    } catch (e) { toast.error(apiError(e)); }
  };

  const counters = useMemo(() => {
    const c = { present: 0, late: 0, absent: 0, unmarked: 0 };
    (data?.roster || []).forEach((r) => {
      const s = attendance[r.ward_id];
      if (s === "present") c.present++;
      else if (s === "late") c.late++;
      else if (s === "absent") c.absent++;
      else c.unmarked++;
    });
    return c;
  }, [attendance, data]);

  const batch = data?.batch;
  const roster = data?.roster || [];

  return (
    <div data-testid="coach-batch-detail">
      <Link to="/coach" className="inline-flex items-center gap-2 text-xs font-mono uppercase tracking-widest text-white/60 hover:brand-blue mb-6">
        <ArrowLeft size={14} /> All batches
      </Link>

      {loading && <div className="text-white/40 font-mono uppercase text-xs">Loading…</div>}

      {batch && (
        <>
          <div className="font-mono text-xs uppercase tracking-[0.3em] brand-orange mb-3">
            // {batch.sport} · {batch.age_group}
          </div>
          <h1 className="font-display text-4xl md:text-5xl font-black uppercase tracking-tighter leading-none" data-testid="coach-batch-title">
            {batch.name}
          </h1>
          <div className="mt-3 flex flex-wrap gap-4 text-xs font-mono text-white/60">
            <span className="flex items-center gap-1"><Clock size={12} /> {batch.time_start} – {batch.time_end}</span>
            <span className="flex items-center gap-1"><Calendar size={12} /> {(batch.days || []).join(", ")}</span>
            <span className="flex items-center gap-1"><Users size={12} /> {roster.length} / {batch.capacity}</span>
          </div>

          {/* Date + KPIs */}
          <div className="mt-8 flex flex-col md:flex-row gap-4 md:items-center md:justify-between">
            <div>
              <div className="text-[10px] font-mono uppercase tracking-widest text-white/40 mb-1">Session date</div>
              <input type="date" value={date} onChange={(e) => setDate(e.target.value)} data-testid="coach-att-date"
                className="bg-[#131A2B] border border-white/20 py-2 px-3 text-sm" />
            </div>
            <div className="flex gap-2 items-center flex-wrap">
              <Counter label="Present" value={counters.present} cls="bg-brand-blue/15 border-brand-blue/40 text-brand-blue" testid="counter-present" />
              <Counter label="Late"    value={counters.late}    cls="bg-yellow-500/15 border-yellow-500/40 text-yellow-400" testid="counter-late" />
              <Counter label="Absent"  value={counters.absent}  cls="bg-red-500/15 border-red-500/40 text-red-400" testid="counter-absent" />
              <Counter label="Unmarked" value={counters.unmarked} cls="border-white/20 text-white/60" testid="counter-unmarked" />
              <button
                onClick={async () => {
                  if (counters.absent === 0) {
                    toast.info("Nobody is marked absent today.");
                    return;
                  }
                  if (!window.confirm(`Send a WhatsApp ping to ${counters.absent} absent parent(s)?`)) return;
                  setPinging(true);
                  try {
                    const { data: r } = await api.post("/coach/ping-absent-parents", { batch_id: batchId, date });
                    toast.success(`Pinged ${r.pinged} parent(s)${r.skipped_no_phone ? ` · ${r.skipped_no_phone} skipped (no phone)` : ""}`);
                  } catch (e) {
                    toast.error(apiError(e));
                  } finally {
                    setPinging(false);
                  }
                }}
                disabled={pinging || counters.absent === 0}
                data-testid="ping-absent-btn"
                title="Send a WhatsApp reminder to every absent ward's parent for this date"
                className="ml-1 inline-flex items-center gap-1.5 border border-green-500/50 text-green-400 hover:bg-green-500 hover:text-white disabled:opacity-40 disabled:cursor-not-allowed px-3 py-2 text-[11px] font-mono uppercase tracking-widest transition"
              >
                <MessageCircle size={12} /> {pinging ? "Pinging…" : `Ping Absents${counters.absent ? ` (${counters.absent})` : ""}`}
              </button>
            </div>
          </div>

          {/* Roster */}
          <div className="mt-8 border border-white/12 bg-[#131A2B] overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="font-mono text-xs uppercase tracking-widest text-white/40 border-b border-white/12">
                  <th className="text-left p-4">Ward</th>
                  <th className="text-left p-4">Parent contact</th>
                  <th className="text-left p-4">Fee plan</th>
                  <th className="text-center p-4">Status</th>
                  <th className="text-right p-4">Mark</th>
                </tr>
              </thead>
              <tbody>
                {roster.map((r) => {
                  const st = attendance[r.ward_id];
                  const meta = STATUS_META[st];
                  return (
                    <tr key={r.enrollment_id} data-testid={`coach-row-${r.ward_id}`} className="border-b border-white/5 align-top">
                      <td className="p-4">
                        <div className="font-bold">{r.ward_name}</div>
                        <div className="text-xs text-white/50 font-mono uppercase tracking-widest">Age {r.age ?? "—"} · {r.sport}</div>
                      </td>
                      <td className="p-4">
                        <div className="font-bold text-sm">{r.parent?.name || "—"}</div>
                        <div className="mt-1 flex flex-col gap-1 text-xs text-white/60">
                          {r.parent?.phone && (
                            <a href={`tel:${r.parent.phone}`} className="inline-flex items-center gap-1.5 hover:brand-blue" data-testid={`call-${r.ward_id}`}>
                              <Phone size={11} /> {r.parent.phone}
                            </a>
                          )}
                          {r.parent?.email && (
                            <a href={`mailto:${r.parent.email}`} className="inline-flex items-center gap-1.5 hover:brand-orange truncate max-w-xs" data-testid={`mail-${r.ward_id}`}>
                              <Mail size={11} /> {r.parent.email}
                            </a>
                          )}
                        </div>
                      </td>
                      <td className="p-4 text-xs font-mono text-white/60">{r.fee_label || "—"}</td>
                      <td className="p-4 text-center">
                        <span className={`px-2 py-1 text-[10px] font-mono uppercase tracking-widest border ${meta ? meta.cls : "border-white/15 text-white/30"}`}>
                          {meta ? meta.label : "Unmarked"}
                        </span>
                      </td>
                      <td className="p-4 text-right space-x-1 whitespace-nowrap">
                        <button onClick={() => mark(r.ward_id, "present")} data-testid={`mark-present-${r.ward_id}`}
                          className="p-2 border border-white/20 hover:border-brand-blue hover:text-brand-blue" title="Present">
                          <Check size={12} />
                        </button>
                        <button onClick={() => mark(r.ward_id, "late")} data-testid={`mark-late-${r.ward_id}`}
                          className="p-2 border border-white/20 hover:border-yellow-400 hover:text-yellow-400" title="Late">
                          <Clock size={12} />
                        </button>
                        <button onClick={() => mark(r.ward_id, "absent")} data-testid={`mark-absent-${r.ward_id}`}
                          className="p-2 border border-white/20 hover:border-red-400 hover:text-red-400" title="Absent">
                          <X size={12} />
                        </button>
                      </td>
                    </tr>
                  );
                })}
                {roster.length === 0 && (
                  <tr><td colSpan={5} className="p-8 text-center text-white/40">No wards enrolled in this batch yet.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}

function Counter({ label, value, cls, testid }) {
  return (
    <div data-testid={testid} className={`border ${cls} px-3 py-2 text-center min-w-[64px]`}>
      <div className="font-display text-xl font-black">{value}</div>
      <div className="text-[9px] font-mono uppercase tracking-widest opacity-80">{label}</div>
    </div>
  );
}
